import { useState } from 'react';
import { Flex, Text, toaster } from '@optiaxiom/react';

import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { useObjectTitle } from '@frontend/hooks';
import { AgentExecutionItem } from '@frontend/types/agentExecution';
import { SpecializedAgentExecution } from '@frontend/types/specialized-agent';
import { StepExecution } from '@frontend/types/stepExecution';

import { useExecutionDetails } from '../../hooks/useAgentExecutions';
import { useCancelWorkflowExecution } from '../../hooks/useCancelWorkflowExecution';
import { useResumeWorkflowExecution } from '../../hooks/useResumeWorkflowExecution';
import { useStepExecutionDetails } from '../../hooks/useStepExecutionDetails';
import { useGetWorkflowAgentWithGraph } from '../../hooks/useWorkflowAgent';
import { getStepExecutionDetailsByStepId } from '../../utils/executionUtils';
import { ExecutionDetailsSidebar } from '../executions/ExecutionDetailsSidebar';
import { SpecializedAgentExecutionDetailsSidebar } from '../specialized-agent/executions/SpecializedAgentExecutionDetailsSidebar';
import { nodeConfigs } from '../workflow-agent/nodes/configs';
import { WorkflowProvider } from '../workflow-agent/providers/WorkflowProvider';

import { ExecutionCanvas } from './ExecutionCanvas';
import { ExecutionHistoryModal } from './ExecutionHistoryModal';
import {
  layoutContainer,
  mainContent,
  portalOverlay,
  sidebarArea,
} from './WorkflowExecutionContent.css';
import { WorkflowExecutionTopbar } from './WorkflowExecutionTopbar';

const adaptStepExecutionToSpecializedExecution = (
  stepExecution: StepExecution,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchedExecution?: any,
  agentId?: string
): SpecializedAgentExecution => {
  return {
    links: {
      self: stepExecution.links?.execution || '',
      collection: '',
      executions: '',
      execute: '',
      delete: '',
      agent: stepExecution.links?.agent || '',
      memory: fetchedExecution?.links?.memory || stepExecution.links?.memory || '',
    },
    id: stepExecution.id || `step-${stepExecution.step_id}`,
    agent_id: agentId || stepExecution.agent?.agent_id || '',
    parameters: (fetchedExecution?.parameters ||
      stepExecution.parameters ||
      stepExecution.input_data ||
      {}) as Record<string, string | number | boolean | Record<string, unknown>>,
    result: {
      response:
        fetchedExecution?.result?.response ||
        stepExecution.result?.response ||
        (stepExecution.output_data ? JSON.stringify(stepExecution.output_data, null, 2) : ''),
    },
    execution_time:
      fetchedExecution?.execution_time ||
      stepExecution.execution_time ||
      stepExecution.result?.execution_time ||
      0,
    token_usage: fetchedExecution?.token_usage ||
      stepExecution.token_usage ||
      stepExecution.result?.token_usage || {
        input_tokens: 0,
        output_tokens: 0,
        total_tokens: 0,
      },
    log: fetchedExecution?.log || [],
    created_at:
      fetchedExecution?.created_at || stepExecution.start_time || new Date().toISOString(),
    memory_id:
      fetchedExecution?.memory_id ||
      stepExecution.memory_id ||
      stepExecution.result?.memory_id ||
      '',
    status: stepExecution.status,
    artifacts: stepExecution.execution?.artifacts || [],
    evaluation_score: fetchedExecution?.evaluation_score ?? null,
    evaluation_feedback: fetchedExecution?.evaluation_feedback ?? null,
  };
};

type WorkflowExecutionContentProps = {
  execution: AgentExecutionItem;
  onClose: () => void;
};

export function WorkflowExecutionContent({ execution, onClose }: WorkflowExecutionContentProps) {
  const [selectedStepId, setSelectedStepId] = useState<string | undefined>();
  const [selectedStepExecution, setSelectedStepExecution] = useState<StepExecution | null>(null);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(true);
  const [isExecutionHistoryModalOpen, setIsExecutionHistoryModalOpen] = useState(false);
  const [modalStepId, setModalStepId] = useState<string>('');
  const [modalExecutions, setModalExecutions] = useState<StepExecution[]>([]);

  const { data: executionDetails, isPending: isLoadingExecution } = useExecutionDetails(
    execution.links.self
  );

  const { agent: workflowAgent, isPending: isLoadingAgent } = useGetWorkflowAgentWithGraph(
    execution.links.workflow_agent,
    true
  );

  // Set document title for the workflow execution
  useObjectTitle(workflowAgent?.name);

  const shouldPollStepExecution =
    selectedStepExecution?.status?.toLowerCase() === 'pending' ||
    selectedStepExecution?.status?.toLowerCase() === 'running';

  const { data: fetchedStepExecution } = useStepExecutionDetails(
    selectedStepExecution,
    shouldPollStepExecution
  );

  const handleStepClick = (stepId: string) => {
    if (!executionDetails) return;
    const allExecutions = getStepExecutionDetailsByStepId(executionDetails, stepId);

    if (allExecutions.length === 0) {
      toaster.create('No execution found for this step', { intent: 'warning' });
      return;
    }
    if (allExecutions.length === 1) {
      handleExecutionSelect(allExecutions[0]);
      return;
    }
    if (allExecutions.length > 0) {
      setModalStepId(stepId);
      setModalExecutions(allExecutions);
      setIsExecutionHistoryModalOpen(true);
    }
  };

  const handleExecutionSelect = (execution: StepExecution) => {
    setSelectedStepId(execution.step_id);
    setSelectedStepExecution(execution);
  };

  const handleCloseExecutionHistoryModal = () => {
    setIsExecutionHistoryModalOpen(false);
    setModalStepId('');
    setModalExecutions([]);
  };

  const handleCloseSidebar = () => {
    setSelectedStepId(undefined);
    setSelectedStepExecution(null);
  };

  const handleMenuClick = () => {
    setIsLeftSidebarOpen(prev => !prev);
  };

  const cancelMutation = useCancelWorkflowExecution();
  const resumeMutation = useResumeWorkflowExecution();

  const handleCancel = () => {
    if (execution.id) {
      cancelMutation.mutate(execution.id);
    }
  };

  const handleResume = () => {
    if (execution.id) {
      resumeMutation.mutate(execution.id);
    }
  };

  return (
    <Flex className={portalOverlay} gap="0">
      <Flex className={layoutContainer} gap="0">
        <WorkflowExecutionTopbar
          onMenuClick={handleMenuClick}
          onClose={onClose}
          isMenuOpen={isLeftSidebarOpen}
          title={workflowAgent?.name || 'Workflow Execution'}
          status={execution.status}
          startTime={execution.start_time}
        />
        <Flex className={sidebarArea}>
          <Sidebar open={isLeftSidebarOpen} initialWidth={420} dock="left" top={0}>
            <ExecutionDetailsSidebar
              execution={execution}
              onClose={() => setIsLeftSidebarOpen(false)}
              onCancel={handleCancel}
              isCancelling={cancelMutation.isPending}
              onResume={handleResume}
              isResuming={resumeMutation.isPending}
            />
          </Sidebar>
          <Flex className={mainContent}>
            {isLoadingExecution || isLoadingAgent ? (
              <Flex
                h="full"
                w="full"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                gap="16"
              >
                <Text>Loading workflow execution...</Text>
              </Flex>
            ) : workflowAgent && executionDetails ? (
              <WorkflowProvider nodeConfigs={nodeConfigs}>
                <ExecutionCanvas
                  nodes={workflowAgent.nodes || []}
                  edges={workflowAgent.edges || []}
                  executionDetails={executionDetails}
                  stepExecutions={executionDetails.step_executions || []}
                  onStepClick={handleStepClick}
                  selectedStepId={selectedStepId}
                />
              </WorkflowProvider>
            ) : (
              <Flex
                h="full"
                w="full"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                gap="16"
              >
                <Text>Unable to load workflow execution data</Text>
              </Flex>
            )}
          </Flex>
          <Sidebar
            open={!!selectedStepExecution}
            dock="right"
            top={0}
            initialWidth={400}
            minWidth={300}
            maxWidth={600}
          >
            {selectedStepExecution && executionDetails && (
              <SpecializedAgentExecutionDetailsSidebar
                {...(selectedStepExecution.execution?.id
                  ? { executionId: selectedStepExecution.execution.id }
                  : {
                      execution: adaptStepExecutionToSpecializedExecution(
                        selectedStepExecution,
                        fetchedStepExecution,
                        executionDetails.workflow_agent_id
                      ),
                    })}
                onClose={handleCloseSidebar}
              />
            )}
          </Sidebar>
        </Flex>
      </Flex>

      {/* Execution History Modal */}
      <ExecutionHistoryModal
        isOpen={isExecutionHistoryModalOpen}
        onClose={handleCloseExecutionHistoryModal}
        executions={modalExecutions}
        onSelectExecution={handleExecutionSelect}
        selectedExecutionId={selectedStepExecution?.id}
        stepId={modalStepId}
      />
    </Flex>
  );
}
